(ns manatee.edit.view
  (:require
   [clojure.set :as set]
   [clojure.string :as string]
   [kee-frame.core :as kf]
   [manatee.admin.components :as admin-components]
   [manatee.admin.rejection-option.subs :as rejection-subs]
   [manatee.admin.subs :as admin-subs]
   [manatee.admin.task-config.subs :as task-config-subs]
   [manatee.admin.view :as admin-view]
   [manatee.annotation.components :as annotation-components]
   [manatee.components.modal.handlers :as modal]
   [manatee.components.table.formatters :as manatee-formatters]
   [manatee.domain.edit :as edit]
   [manatee.domain.task :as task]
   [manatee.edit.handlers :as handlers]
   [manatee.edit.subs :as subs]
   [manatee.qa.handlers :as qa-handlers]
   [manatee.qa.subs :as qa-subs]
   [manatee.ui.templates :as templates]
   [manatee.user.subs :as user-subs]
   [medley.core :as medley]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [rothko.button.component :refer [button]]
   [rothko.collection-components.core :as collection]
   [rothko.collection-components.filters.component :as filters]
   [rothko.collection-components.table.component :as table]
   [rothko.form2.component :as form]
   [rothko.form2.select.core :as select]
   [rothko.form2.select.multi-select.component :as multi-select]
   [rothko.form2.select.single-select.component :as single-select]
   [rothko.icon-animated.component :refer [spinner]]
   [rothko.layout.component :refer [layout]]
   [rothko.nav.component :refer [nav]]
   [rothko.panel.component :refer [panel]]
   [rothko.tooltip.component :as tooltip]))

(def status-form-options
  [{:value "done"
    :representation "Done"}
   {:value "rejected"
    :representation "Rejected"}
   {:value "under-review"
    :representation "Under review"}
   {:value "requires-approval"
    :representation "Requires approval"}
   {:value "rejected-requires-approval"
    :representation "Rejected, requires approval"}])

(def role-form-options
  [{:value "admin"
    :representation "Admin"}
   {:value "annotator"
    :representation "Annotator"}])

(defn reason-form-options
  "Transform text-annotation rejection reasons to form options.
  There is no requirement (yet) for speech-annotation rejection reasons"
  [rejection-reasons]
  (map (fn [o]
         (-> o
             (assoc :value (name (:value o)))
             (set/rename-keys  {:label :representation})))
       rejection-reasons))

(def boolean-options
  [{:value true
    :representation "True"}
   {:value false
    :representation "False"}])

(def page-size-options
  (map (fn [v] {:value v :representation (str v)}) [10 25 50 75 100 150 200]))

(def pagination-fields
  {:appearance #{:narrow :text-align-centre}
   :rothko.form2.select.core/options page-size-options
   ::form/placeholder "Size..."})

(def users-to-form-options
  (partial
   map
   (fn [{:keys [first-name last-name email id]}]
     {:value id
      :representation (str first-name " " last-name)
      :tooltip email})))

(defn date-field
  [field-name]
  (fn [_ field]
    [:div
     (when-let [value (get field field-name)]
       (.toLocaleString value
                        "en-GB"
                        #js {:timeStyle "short"
                             :dateStyle "short"}))]))

(defn reason-table-formatter
  [rejection-reasons]
  (fn [_ {:keys [reason other-reason]}]
    [tooltip/tooltip {::tooltip/label other-reason
                      :appearance #{:plain-black :centered-triangle}}
     [:p (edit/rejection-reason->string rejection-reasons reason)]]))

(defn completed-edits-table
  [{:keys [edits total users viewed-items]} {:keys [tasks-form-options task-configs]}]
  (let [users-form-options (users-to-form-options users)
        rejection-reasons @(rf/subscribe [::rejection-subs/rejection-options :answer :text])
        suppress-part-screen? (fn [task-config-id] (task/modifier (get task-configs task-config-id) :suppress-part-screen?))]
    [table/component
     {:appearance #{:primary}
      ::collection/update #(rf/dispatch [::handlers/load-edits %])
      ::collection/items (remove nil? edits)
      ::collection/total total
      ::table/footer true
      ::collection/pagination pagination-fields
      ::collection/fields [{::collection/label "Item Group"
                            ::table/formatter (fn [_ {:keys [item-group-id task-config-id answer-item-id user-id]}]
                                                [manatee-formatters/id-field
                                                 {:id item-group-id
                                                  :path (if (and (suppress-part-screen? task-config-id) answer-item-id)
                                                          [:manatee.qa.handlers/user-item-edits :item-id answer-item-id :user-id user-id]
                                                          [:manatee.qa.handlers/item-group :item-group-id item-group-id :user-id user-id])}])}
                           {::collection/label "Seen"
                            ::table/formatter (manatee-formatters/qa-item-viewed-field
                                               viewed-items
                                               (fn [{:keys [task-config-id]}]
                                                 (if (suppress-part-screen? task-config-id)
                                                   [:answer-item-id :item-id]
                                                   [:item-id])))}
                           {::collection/id :created
                            ::table/ordered? true
                            ::collection/label "Created"
                            ::table/formatter (date-field :created)}
                           {::collection/label "User"
                            ::table/formatter manatee-formatters/user-field
                            ::filters/filter {:appearance #{:primary :pills-inside-input}
                                              ::form/placeholder "User..."
                                              ::form/id :users
                                              ::form/component multi-select/field
                                              ::select/options users-form-options}}
                           {::collection/id :status
                            ::collection/label "Status"
                            ::filters/filter {:appearance #{:primary :pills-inside-input}
                                              ::form/id :status
                                              ::form/placeholder "Status..."
                                              ::form/component multi-select/field
                                              ::select/options status-form-options}}
                           {::collection/id :item-task
                            ::collection/label "Task"
                            ::filters/filter {:appearance #{:primary :pills-inside-input}
                                              ::form/id :tasks
                                              ::form/placeholder "Task..."
                                              ::form/component multi-select/field
                                              ::select/options tasks-form-options}}
                           {::collection/id :reason
                            ::collection/label "Reason"
                            ::table/formatter (reason-table-formatter rejection-reasons)
                            ::filters/filter {:appearance #{:primary :pills-inside-input}
                                              ::form/id :reason
                                              ::form/placeholder "Reason..."
                                              ::form/component multi-select/field
                                              ::select/options (reason-form-options rejection-reasons)}}
                           {::collection/id :has-feedback?
                            ::collection/label "Feedback?"
                            ::table/formatter (manatee-formatters/transform-field
                                               :group-has-feedback? ;sql query column name
                                               (comp string/capitalize str))
                            ::filters/filter {:appearance #{:primary :pills-inside-input}
                                              ::form/id :feedback-edits?
                                              ::form/placeholder "Feedback..."
                                              ::form/component multi-select/field
                                              ::select/options boolean-options}}
                           {::collection/id :feedback-actioned?
                            ::collection/label "Feedback Actioned?"
                            ::table/formatter (manatee-formatters/transform-field
                                               :group-has-feedback-actioned?
                                               (comp string/capitalize str))
                            ::filters/filter {:appearance #{:primary :pills-inside-input}
                                              ::form/id :group-has-feedback-actioned?
                                              ::form/placeholder "Feedback Actioned..."
                                              ::form/component multi-select/field
                                              ::select/options boolean-options}}
                           {::collection/id :evaluator-edit?
                            ::collection/label "Evaluator?"
                            ::table/formatter (manatee-formatters/transform-field
                                               :evaluator-edit?
                                               (comp string/capitalize str))
                            ::filters/filter {:appearance      #{:primary :pills-inside-input}
                                              ::form/id        :evaluator-edits?
                                              ::form/placeholder "Evaluator..."
                                              ::form/component multi-select/field
                                              ::select/options boolean-options}}]}]))

(defn reported-edits-table
  [{:keys [edits total users]} {:keys [tasks-form-options task-configs]}]
  (let [users-form-options (users-to-form-options users)]
    [table/component
     {:appearance #{:primary}
      ::collection/update #(rf/dispatch [::handlers/load-edits %])
      ::collection/items (remove nil? edits)
      ::collection/total total
      ::table/footer true
      ::collection/pagination pagination-fields
      ::collection/fields [{::collection/label "Item Group"
                            ::table/formatter (fn [_ {:keys [item-group-id task-config-id item-id user-id]}]
                                                [manatee-formatters/id-field
                                                 {:id item-group-id
                                                  :path (if (task/modifier (get task-configs task-config-id) :suppress-part-screen?)
                                                          [:manatee.qa.handlers/user-item-reports :item-id item-id :user-id user-id]
                                                          [:manatee.qa.handlers/item-group :item-group-id item-group-id :user-id user-id])}])}
                           {::collection/id :created
                            ::table/ordered? true
                            ::collection/label "Created"
                            ::table/formatter (date-field :created)}
                           {::collection/label "User"
                            ::table/formatter manatee-formatters/user-field
                            ::filters/filter {:appearance #{:primary :pills-inside-input}
                                              ::form/placeholder "User..."
                                              ::form/id :users
                                              ::form/component multi-select/field
                                              ::select/options users-form-options}}
                           {::collection/id :item-task
                            ::collection/label "Task"
                            ::filters/filter {:appearance #{:primary :pills-inside-input}
                                              ::form/id :tasks
                                              ::form/placeholder "Task..."
                                              ::form/component multi-select/field
                                              ::select/options tasks-form-options}}
                           {::collection/id :item-type
                            ::collection/label "Type"}
                           {::collection/id :role
                            ::collection/label "Role"
                            ::filters/filter {:appearance #{:primary :pills-inside-input}
                                              ::form/id :roles
                                              ::form/placeholder "Role..."
                                              ::form/component multi-select/field
                                              ::select/options role-form-options}}
                           {::collection/id :evaluator-edit?
                            ::collection/label "Evaluator?"
                            ::table/formatter (manatee-formatters/transform-field
                                               :evaluator-edit?
                                               (comp string/capitalize str))
                            ::filters/filter {:appearance #{:primary :pills-inside-input}
                                              ::form/id :evaluator-edits?
                                              ::form/component multi-select/field
                                              ::select/options boolean-options}}]}]))

(defn comparate-edits-table
  [{:keys [edits total]} {:keys [task-configs]}]
  (let [{:keys [task-config-id user-a user-b]} @(rf/subscribe [::subs/compare-options])
        {:keys [suppress-part-screen?]} (task/modifiers (get task-configs task-config-id))]
    [table/component
     {:appearance #{:primary :qa-compare-table}
      ::collection/update #(rf/dispatch [::handlers/load-edits %])
      ::collection/items (remove nil? edits)
      ::collection/total total
      ::table/footer true
      ::collection/pagination pagination-fields
      ::collection/fields [{::collection/label "Item Group"
                            ::table/formatter (fn [_ {:keys [item-group-id answer-item-id]}]
                                                [manatee-formatters/id-field
                                                 {:id item-group-id
                                                  :path (if (and suppress-part-screen? answer-item-id)
                                                          [:manatee.qa.handlers/compare-item-edits :item-id answer-item-id :user-a user-a :user-b user-b]
                                                          [:manatee.qa.handlers/compare-item-group :item-group-id item-group-id :user-a user-a :user-b user-b])}])}
                           {::collection/id :created-a
                            ::table/ordered? true
                            ::collection/label "Created A"
                            ::table/formatter (date-field :created-a)}
                           {::collection/id :created-b
                            ::table/ordered? true
                            ::collection/label "Created B"
                            ::table/formatter (date-field :created-b)}
                           {::collection/id :status-a
                            ::collection/label "Status A"}
                           {::collection/id :status-b
                            ::collection/label "Status B"}]}]))

(def tabs
  [{:id :completed
    :title "Completed"
    :icon :tick}
   {:id :reported
    :title "Reported"
    :icon :user}
   {:id :compare
    :title "Compare"
    :icon :questions}])

(defn tab-component
  [tabs selected-tab]
  [nav {:appearance #{:horizontal}
        :menu-toggle-handler #(rf/dispatch [::handlers/select-tab %])
        :items (for [{:keys [id title icon]} tabs]
                 {:key id
                  :selected? (= id selected-tab)
                  :title title
                  :icon icon
                  :route [id]})
        :route-handler (fn [[tab-key]]
                         (kf/path-for [::handlers/edits {:query-params {:edit-source tab-key
                                                                        :tab (name tab-key)}}]))}])

(defn approve-all-button
  [edits]
  (when (seq edits)
    [button {:appearance #{:primary :full-width}
             :on-click #(rf/dispatch [::modal/show :approve-multi-modal])}
     "Approve All"]))

(defn compare-options
  [{:keys [users]}]
  (let [task-config-options (->> @(rf/subscribe [::task-config-subs/active-task-configs])
                                 (filter #(task/modifier % :multi-marking?))
                                 admin-view/task-configs->select-options)
        user-options (users-to-form-options users)
        {:keys [task-config-id user-a user-b]} @(rf/subscribe [::subs/compare-options])
        set-option-fn (fn [k] (fn [v] (rf/dispatch [::handlers/set-compare-option k v])))]
    ^{:key (str (hash task-config-options) (hash user-options))}
    [layout {:appearance #{:row}
             :class [:qa-compare-options]}
     [single-select/field
      (merge
       (admin-components/form-field-props task-config-id (set-option-fn :task-config-id))
       {::select/options task-config-options
        ::form/placeholder "Choose task..."})]
     [single-select/field
      (merge
       (admin-components/form-field-props user-a (set-option-fn :user-a))
       {::select/options user-options
        ::form/placeholder "Choose annotator A..."})]
     [single-select/field
      (merge
       (admin-components/form-field-props user-b (set-option-fn :user-b))
       {::select/options user-options
        ::form/placeholder "Choose annotator B..."})]]))

(defn edits-container
  []
  (let [user-team-filter (r/atom :no-team-filter)
        show-ai-hybrid-only? (r/atom false)]
    (fn []
      (let [current-user @(rf/subscribe [::user-subs/user])
            project-owner? @(rf/subscribe [::user-subs/project-owner?])
            user-teams @(rf/subscribe [::admin-subs/user-teams])
            _ (when project-owner?
                (reset! user-team-filter (:representation (first (filter #(= (:value %)
                                                                             (:team current-user))
                                                                         user-teams)))))
            selected-tab @(rf/subscribe [::subs/selected-tab])
            filtered-edits @(rf/subscribe [::subs/edits-by-idents])
            edits-to-approve (filter (fn [edit] (#{:requires-approval :rejected-requires-approval} (:status edit))) (vals filtered-edits))
            {:keys [title]} (medley/find-first #(= (:id %) selected-tab) tabs)
            {:keys [edits total]} @(rf/subscribe [::subs/edits-table])
            viewed-items @(rf/subscribe [::qa-subs/viewed-items])
            task-configs @(rf/subscribe [::task-config-subs/task-configs-map])
            tasks-form-options (->> (vals task-configs)
                                    (remove (fn [{:keys [data]}] (get-in data [:modifiers :archive-task?])))
                                    (map (fn [{:keys [task-name]}] {:value task-name :representation task-name}))
                                    (sort #(compare (-> %1 :representation (string/lower-case))
                                                    (-> %2 :representation (string/lower-case))))
                                    (distinct))
            users @(rf/subscribe [::admin-subs/active-users])
            table-component-loading @(rf/subscribe [::subs/table-component-loading])
            table-component (case selected-tab
                              :reported reported-edits-table
                              :compare comparate-edits-table
                              completed-edits-table)
            filters-changed (fn [_ _ old new]
                              (prn :filters-changed old :-> new)
                              (when (not= old new)
                                (rf/dispatch [::handlers/load-edits])))]
        (add-watch user-team-filter :on-change-filter filters-changed)
        (add-watch show-ai-hybrid-only? :on-change-filter filters-changed)
        [:div
         [annotation-components/alert-container]
         [annotation-components/approve-multi-modal edits-to-approve]
         [tab-component tabs selected-tab]
         [panel {:appearance #{:primary}
                 :class #{:allow-overflow}}
          [:h4 title]
          (if (= selected-tab :compare)
            [compare-options {:users users}]
            [:div {:style {:display :grid :grid-direction :row}}
             (when-not project-owner?
               [admin-view/team-filter user-teams user-team-filter])
             [:div.checkbox__container
              {:class [:manatee]}
              [:input
               {:type           :checkbox
                :name           :ai-hybrid-only
                :on-change      #(do (rf/dispatch [::qa-handlers/set-aihyb-filter (not @show-ai-hybrid-only?)])
                                     (swap! show-ai-hybrid-only? not))
                :disabled       false
                :defaultChecked false}]
              [:label {:for :ai-hybrid-only} "AI-Hybrid only"]]])
          (if table-component-loading
            [spinner {:size 40}]
            [table-component
             {:edits        edits
              :total        total
              :users        users
              :viewed-items viewed-items}
             {:tasks-form-options tasks-form-options
              :task-configs task-configs}])
          [approve-all-button edits-to-approve]]]))))

(def edits-page
  {:template templates/single-column
   :main edits-container})
